import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { useAuthState } from '@/hooks/useAuthState';
import { UserService } from '@/services/userService';
import AdminLayout from '@/layouts/AdminLayout';
import { ArrowLeft, Save, User, Calendar, Hash, CreditCard } from 'lucide-react';
import SubscriptionPlanBadge from '@/components/SubscriptionPlanBadge';
import { getPlanMeta } from '@/lib/subscriptionPlanMeta';
import type { SubscriptionPlanOption, UpdateUser } from '@/integrations/mysql/types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CountrySelect, Country } from '@/components/CountrySelect';
import InputMask from 'react-input-mask';

interface User {
  id: number;
  name: string;
  email: string;
  phone?: string;
  user_type: string;
  plan_id?: number;
  subscription_plan?: string;
  profile_photo?: string;
  created_at: string;
  is_active: boolean;
}

const AdminEditUser = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const { isAuthenticated, userType, userName, userPhoto, userId, loading: authLoading } = useAuthState();
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<User | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    user_type: 'guest',
    plan_id: '1',
    is_active: true,
    password: '',
    confirmPassword: ''
  });
  const [phoneDdi, setPhoneDdi] = useState('55'); // Brasil como padrão
  const [plans, setPlans] = useState<SubscriptionPlanOption[]>([]);

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Verificar autenticação (AdminLayout já faz isso)

  useEffect(() => {
    if (!authLoading && userType === 'admin') {
      UserService.listSubscriptionPlans()
        .then(setPlans)
        .catch(() => setPlans([]));
    }
  }, [authLoading, userType]);

  // Carregar dados do usuário
  useEffect(() => {
    if (id && !authLoading) {
      loadUser();
    }
  }, [id, authLoading]);

  const loadUser = async () => {
    try {
      setLoading(true);
      const userData = await UserService.getUserById(parseInt(id!));
      if (!userData) return;
      setUser(userData as User);
      const planId = (userData as User).plan_id ?? 1;
      setFormData({
        name: userData.name,
        email: userData.email,
        phone: userData.phone || '',
        user_type: userData.user_type,
        plan_id: String(planId),
        is_active: userData.is_active,
        password: '',
        confirmPassword: ''
      });
      // Carregar DDI do usuário ou usar padrão
      setPhoneDdi((userData as any).phone_ddi || '55');
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message || "Erro ao carregar usuário",
        variant: "destructive",
      });
      navigate('/admin/usuarios');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <AdminLayout>
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Carregando usuário...</p>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!isAuthenticated || userType !== 'admin') {
    return (
      <AdminLayout>
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <p className="text-muted-foreground">Acesso negado. Redirecionando...</p>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Função para validar senha forte
  const validatePasswordStrength = (password: string): { isValid: boolean; error?: string } => {
    if (password.length < 8) {
      return { isValid: false, error: 'Senha deve ter pelo menos 8 caracteres' };
    }
    
    if (!/[a-z]/.test(password)) {
      return { isValid: false, error: 'Senha deve conter pelo menos 1 letra minúscula (a-z)' };
    }
    
    if (!/[A-Z]/.test(password)) {
      return { isValid: false, error: 'Senha deve conter pelo menos 1 letra maiúscula (A-Z)' };
    }
    
    if (!/[0-9]/.test(password)) {
      return { isValid: false, error: 'Senha deve conter pelo menos 1 número (0-9)' };
    }
    
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      return { isValid: false, error: 'Senha deve conter pelo menos 1 caractere especial (!@#$%^&*...)' };
    }
    
    return { isValid: true };
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    // Validar senha apenas se foi preenchida
    if (formData.password && formData.password.trim() !== '') {
      // Validar força da senha
      const passwordValidation = validatePasswordStrength(formData.password);
      if (!passwordValidation.isValid) {
        newErrors.password = passwordValidation.error || 'Senha inválida';
      }
      
      // Validar se as senhas coincidem
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'As senhas não coincidem';
      }
    } else if (formData.confirmPassword && formData.confirmPassword.trim() !== '') {
      // Se confirmPassword foi preenchido mas password não
      newErrors.password = 'Preencha a nova senha';
    }

    if (!formData.user_type) {
      newErrors.user_type = 'Tipo de usuário é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'user_type' && value === 'guest') {
        next.plan_id = '1';
      }
      return next;
    });
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const availablePlans =
    formData.user_type === 'owner'
      ? plans
      : plans.filter((p) => p.slug === 'freemium');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const updateData: UpdateUser & { phone_ddi?: string } = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        phone_ddi: phoneDdi,
        user_type: formData.user_type as 'guest' | 'owner',
        plan_id: parseInt(formData.plan_id, 10),
        is_active: formData.is_active,
      };

      if (formData.password && formData.password.trim() !== '' && formData.password === formData.confirmPassword) {
        updateData.password = formData.password.trim();
      } else if (formData.password && formData.password.trim() !== '') {
        toast({
          title: "Erro",
          description: "As senhas não coincidem",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      await UserService.updateUser(parseInt(id!), updateData);

      toast({
        title: "Sucesso",
        description: "Usuário atualizado com sucesso!",
      });

      navigate('/admin/usuarios');
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message || "Erro ao atualizar usuário",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/usuarios')}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar para Usuários
          </Button>
          
          <h1 className="text-3xl font-bold text-gray-900">Editar Usuário</h1>
          <p className="text-gray-600 mt-2">Editar informações do usuário no sistema</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Informações do Usuário
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* ID do Usuário */}
                <div className="space-y-2">
                  <Label htmlFor="id">ID do Usuário</Label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="id"
                      value={user?.id || ''}
                      disabled
                      className="pl-10 bg-gray-50"
                    />
                  </div>
                </div>

                {/* Data de Criação */}
                <div className="space-y-2">
                  <Label htmlFor="created_at">Criado em</Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="created_at"
                      value={user?.created_at ? format(new Date(user.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR }) : ''}
                      disabled
                      className="pl-10 bg-gray-50"
                    />
                  </div>
                </div>

                {/* Nome */}
                <div className="space-y-2">
                  <Label htmlFor="name">Nome Completo *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Digite o nome completo"
                    className={errors.name ? 'border-red-500' : ''}
                  />
                  {errors.name && (
                    <p className="text-sm text-red-500">{errors.name}</p>
                  )}
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="usuario@exemplo.com"
                    className={errors.email ? 'border-red-500' : ''}
                  />
                  {errors.email && (
                    <p className="text-sm text-red-500">{errors.email}</p>
                  )}
                </div>

                {/* Telefone */}
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefone</Label>
                  <div className="flex gap-2">
                    <CountrySelect
                      value={phoneDdi}
                      onValueChange={(ddi: string, country: Country) => {
                        setPhoneDdi(ddi);
                      }}
                      disabled={loading}
                    />
                    <InputMask
                      mask="(99) 99999-9999"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      disabled={loading}
                      className="flex-1"
                    >
                      {(inputProps: any) => (
                        <Input
                          {...inputProps}
                          id="phone"
                          placeholder="(81) 99999-9999"
                          className="flex-1"
                        />
                      )}
                    </InputMask>
                  </div>
                </div>

                {/* Tipo de Usuário */}
                <div className="space-y-2">
                  <Label htmlFor="user_type">Tipo de Usuário *</Label>
                  <Select
                    value={formData.user_type}
                    onValueChange={(value) => handleInputChange('user_type', value)}
                  >
                    <SelectTrigger className={errors.user_type ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="guest">Hóspede</SelectItem>
                      <SelectItem value="owner">Proprietário</SelectItem>
                      <SelectItem value="admin">Administrador</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.user_type && (
                    <p className="text-sm text-red-500">{errors.user_type}</p>
                  )}
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="plan_id" className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4" />
                    Plano de assinatura
                  </Label>
                  <div className="flex flex-wrap items-center gap-3">
                    <Select
                      value={formData.plan_id}
                      onValueChange={(value) => handleInputChange('plan_id', value)}
                      disabled={formData.user_type === 'guest'}
                    >
                      <SelectTrigger id="plan_id" className="w-full max-w-md">
                        <SelectValue placeholder="Selecione o plano" />
                      </SelectTrigger>
                      <SelectContent>
                        {availablePlans.map((plan) => (
                          <SelectItem key={plan.id} value={String(plan.id)}>
                            {plan.name}
                            {plan.slug === 'live' && ' — acesso completo'}
                            {plan.slug === 'porto2life' && ' — Porto 2 Life'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <SubscriptionPlanBadge
                      plan={getPlanMeta(null, parseInt(formData.plan_id, 10)).slug}
                      planId={parseInt(formData.plan_id, 10)}
                      size="md"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {formData.user_type === 'guest'
                      ? 'Hóspedes permanecem no plano Essencial.'
                      : 'Live libera todas as funcionalidades; Porto 2 Life é focado no empreendimento Porto 2 Life.'}
                  </p>
                </div>

                {/* Senha */}
                <div className="space-y-2">
                  <Label htmlFor="password">Nova Senha (opcional)</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    placeholder="Deixe em branco para manter a senha atual"
                    className={errors.password ? 'border-red-500' : ''}
                  />
                  {!errors.password && formData.password && (
                    <p className="text-xs text-gray-500">
                      Mínimo 8 caracteres, 1 minúscula, 1 maiúscula, 1 número e 1 caractere especial
                    </p>
                  )}
                  {errors.password && (
                    <p className="text-sm text-red-500">{errors.password}</p>
                  )}
                </div>

                {/* Confirmar Senha */}
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirmar Nova Senha (opcional)</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    placeholder="Confirme a nova senha"
                    className={errors.confirmPassword ? 'border-red-500' : ''}
                  />
                  {errors.confirmPassword && (
                    <p className="text-sm text-red-500">{errors.confirmPassword}</p>
                  )}
                </div>
              </div>

              {/* Status Ativo */}
              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => handleInputChange('is_active', checked)}
                />
                <Label htmlFor="is_active">Usuário ativo</Label>
              </div>

              {/* Botões */}
              <div className="flex justify-end space-x-4 pt-6 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/admin/usuarios')}
                  disabled={loading}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={loading}>
                  <Save className="h-4 w-4 mr-2" />
                  {loading ? 'Salvando...' : 'Salvar Alterações'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminEditUser;